/**
 * JWT Utilities - Decodificação e validação de tokens JWT
 * Baseado na estrutura enriquecida do backend
 */
/**
 * Decodifica um JWT token sem verificar a assinatura
 * ATENÇÃO: Apenas para extrair payload, não para validação de segurança
 */
export const decodeJwt = (token) => {
    try {
        if (!token || typeof token !== 'string') {
            return null;
        }
        // JWT tem 3 partes separadas por pontos: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        // Decodificar o payload (segunda parte)
        const payload = parts[1];
        // Adicionar padding se necessário para base64
        const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
        // Decodificar base64
        const decodedPayload = atob(paddedPayload);
        // Parse JSON
        const parsedPayload = JSON.parse(decodedPayload);
        // Validar estrutura mínima
        if (!parsedPayload.sub || !parsedPayload.email || !parsedPayload.accountId) {
            console.warn('JWT payload inválido: campos obrigatórios ausentes');
            return null;
        }
        return parsedPayload;
    }
    catch (error) {
        console.error('Erro ao decodificar JWT:', error);
        return null;
    }
};
/**
 * Verifica se um token JWT está expirado
 */
export const isTokenExpired = (token) => {
    try {
        const payload = decodeJwt(token);
        if (!payload || !payload.exp) {
            return true;
        }
        // exp está em segundos, Date.now() em milissegundos
        const currentTime = Math.floor(Date.now() / 1000);
        return payload.exp < currentTime;
    }
    catch (error) {
        console.error('Erro ao verificar expiração do token:', error);
        return true;
    }
};
/**
 * Obtém a data de expiração de um token JWT
 */
export const getTokenExpiration = (token) => {
    try {
        const payload = decodeJwt(token);
        if (!payload || !payload.exp) {
            return null;
        }
        // Converter de segundos para milissegundos
        return new Date(payload.exp * 1000);
    }
    catch (error) {
        console.error('Erro ao obter expiração do token:', error);
        return null;
    }
};
/**
 * Verifica se um token expira em X minutos
 */
export const willExpireIn = (token, minutes) => {
    try {
        const expirationDate = getTokenExpiration(token);
        if (!expirationDate) {
            return true;
        }
        const now = new Date();
        const timeUntilExpiration = expirationDate.getTime() - now.getTime();
        const minutesUntilExpiration = timeUntilExpiration / (1000 * 60);
        return minutesUntilExpiration <= minutes;
    }
    catch (error) {
        console.error('Erro ao verificar tempo de expiração:', error);
        return true;
    }
};
/**
 * Extrai informações básicas do usuário do JWT
 */
export const getUserFromJwt = (token) => {
    const payload = decodeJwt(token);
    if (!payload)
        return null;
    return {
        id: payload.sub,
        email: payload.email,
        accountId: payload.accountId,
        role: payload.role,
        plan: payload.plan,
        features: payload.features || []
    };
};
/**
 * Valida se um JWT tem a estrutura esperada
 */
export const isValidJwtStructure = (token) => {
    try {
        const payload = decodeJwt(token);
        if (!payload)
            return false;
        // Verificar campos obrigatórios
        const requiredFields = ['sub', 'email', 'accountId', 'role'];
        for (const field of requiredFields) {
            if (!payload[field]) {
                return false;
            }
        }
        // Verificar se features é array
        if (payload.features && !Array.isArray(payload.features)) {
            return false;
        }
        return true;
    }
    catch (error) {
        return false;
    }
};
/**
 * Obtém tempo restante até expiração em formato legível
 */
export const getTimeUntilExpiration = (token) => {
    try {
        const expirationDate = getTokenExpiration(token);
        if (!expirationDate) {
            return 'Token inválido';
        }
        const now = new Date();
        const timeLeft = expirationDate.getTime() - now.getTime();
        if (timeLeft <= 0) {
            return 'Expirado';
        }
        const minutes = Math.floor(timeLeft / (1000 * 60));
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        if (days > 0) {
            return `${days}d ${hours % 24}h`;
        }
        else if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        }
        else {
            return `${minutes}m`;
        }
    }
    catch (error) {
        return 'Erro';
    }
};
/**
 * Debug: Mostra informações do JWT no console
 */
export const debugJwt = (token) => {
    console.group('🔍 JWT Debug');
    const payload = decodeJwt(token);
    if (!payload) {
        console.error('❌ Token inválido');
        console.groupEnd();
        return;
    }
    console.log('✅ Token válido');
    console.log('👤 Usuário:', payload.sub, '(' + payload.email + ')');
    console.log('🏢 Conta:', payload.accountId);
    console.log('🎭 Role:', payload.role);
    console.log('💳 Plano:', payload.plan?.name || 'N/A');
    console.log('⚡ Features:', payload.features || []);
    console.log('⏰ Expira em:', getTimeUntilExpiration(token));
    console.log('🕐 Expirado?', isTokenExpired(token) ? '❌ Sim' : '✅ Não');
    console.groupEnd();
};
